import { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { fetchData, deleteData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { Fade, Tooltip, TextField, InputAdornment, CircularProgress, Backdrop } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import Pagination from '../../common/Pagination';
import RentInvoiceModal from '../../pages/Delivery/RentInvoiceModal';
import RentInvoicePDFViewer from './RentInvoicePDFViewer';
import { getCompanyConfigFromAPI } from '../Sale/CompanyConfig';

function RentInvoiceIndex() {
  const [data, setData] = useState([]);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const { isAuthenticated, companyId, branchId, logout, token } = useAuth();

  // PDF Viewer states
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [selectedInvoiceForPDF, setSelectedInvoiceForPDF] = useState(null);
  const [selectedInvoiceDetailsForPDF, setSelectedInvoiceDetailsForPDF] = useState([]);
  const [companyData, setCompanyData] = useState(null);
  const [items, setItems] = useState([]);

  // Modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('view');
  const [selectedItem, setSelectedItem] = useState(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Date filter states
  const [fromDate, setFromDate] = useState(() => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    return firstDay.toISOString().split('T')[0];
  });
  const [toDate, setToDate] = useState(() => {
    const now = new Date();
    return now.toISOString().split('T')[0];
  });

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setMounted(true);
    if (isAuthenticated && tokenIsExpired(token)) {
      logout();
      return;
    }
  }, [isAuthenticated, companyId]);

  useEffect(() => {
    if (loading || !companyId || !token) return;
    setLoading(true);
    Promise.all([
      fetchTableData(),
      fetchCompanyData(),
      fetchItems()
    ]).finally(() => {
      setLoading(false);
    });
  }, [companyId, token]);

  useEffect(() => {
    if (companyId && token && !loading) {
      fetchTableData();
    }
  }, [fromDate, toDate]);

  const fetchTableData = async () => {
    try {
      const response = await fetchData('RentInvoiceApi', `/RentInvoiceList/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
      console.log('RentInvoice API Response:', response);
      
      if (response && response.responseCode === '1111' && response.response_data) {
        setData(response.response_data);
        console.log('RentInvoice data loaded:', response.response_data);
      } else {
        console.log('No data or invalid response');
        setData([]);
      }
    } catch (error) {
      console.error('Error fetching Rent Invoice Details:', error);
      setData([]);
    }
  };

  const fetchCompanyData = async () => {
    try {
      console.log('Fetching company data from API...', { companyId, branchId });
      const apiCompanyData = await getCompanyConfigFromAPI(companyId, branchId, token);
      setCompanyData(apiCompanyData);
      console.log('Company data fetched successfully:', apiCompanyData);
    } catch (error) {
      console.error('Failed to fetch company data from API:', error);
      setCompanyData(null);
    }
  };

  const fetchItems = async () => {
    try {
      const response = await fetchData('ItemApi', `/${companyId}`, token);
      setItems(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching items:', error);
      setItems([]);
    }
  };

  const handleDelete = async () => {
    try {
      const response = await deleteData('RentInvoiceApi', deleteItemId, token);
      if (response && response.responseCode === '1111') {
        await fetchTableData();
      }
    } catch (error) {
      console.error('Error deleting Rent Invoice:', error);
    }
    setConfirmDialogOpen(false);
    setDeleteItemId(null);
  };

  const openConfirmDialog = (itemId) => {
    setDeleteItemId(itemId);
    setConfirmDialogOpen(true);
  };

  const closeConfirmDialog = () => {
    setDeleteItemId(null);
    setConfirmDialogOpen(false);
  };

  const fetchSingleInvoice = async (invoiceId) => {
    try {
      const response = await fetchData('RentInvoiceApi', `/${invoiceId}`, token);
      console.log('Single Invoice Response:', response);
      let invoiceData = response.response_data || response;
      if (invoiceData.RentInvoice && invoiceData.RentInvoiceDetails) {
        return {
          ...invoiceData.RentInvoice,
          RentInvoiceDetails: invoiceData.RentInvoiceDetails
        };
      }
      return invoiceData;
    } catch (error) {
      console.error('Error fetching single invoice:', error);
      return null;
    }
  };

  const handleOpenModal = async (mode, item = null) => {
    if (item) {
      setModalLoading(true);
      const fullData = await fetchSingleInvoice(item.Id);
      setSelectedItem(fullData);
      setModalLoading(false);
    } else {
      setSelectedItem(null);
    }
    setModalMode(mode);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedItem(null);
  };

  // PDF handlers
  const handleOpenPDFViewer = async (invoiceId) => {
    setLoading(true);
    try {
      const freshData = await fetchSingleInvoice(invoiceId);
      if (freshData) {
        const invoiceData = freshData.RentInvoice || freshData;
        const details = freshData.RentInvoiceDetails || [];
        
        setSelectedInvoiceForPDF(invoiceData);
        setSelectedInvoiceDetailsForPDF(details);
        setPdfViewerOpen(true);
      }
    } catch (error) {
      console.error('Error fetching invoice for PDF:', error);
    }
    setLoading(false);
  };

  const handleClosePDFViewer = () => {
    setPdfViewerOpen(false);
    setSelectedInvoiceForPDF(null);
    setSelectedInvoiceDetailsForPDF([]);
  };

  // WhatsApp handler
  const handleWhatsApp = (item) => {
    const message = `*Rent Invoice*\n\nBill No: ${item.BillNumber}\nCustomer: ${item.CustomerName}\nLocation: ${item.Location || 'N/A'}\nTotal Amount: ₹${(item.TotalAmount || 0).toLocaleString('en-IN')}\nPaid Amount: ₹${(item.PaidAmount || 0).toLocaleString('en-IN')}\nBalance: ₹${(item.BalanceAmount || 0).toLocaleString('en-IN')}`;
    const encodedMessage = encodeURIComponent(message);
    const phoneNumber = item.ContactNumber1?.replace(/[^0-9]/g, '');
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const filteredData = data.filter((item) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      item.CustomerName?.toLowerCase().includes(searchLower) ||
      item.ContactNumber1?.toLowerCase().includes(searchLower) ||
      item.Location?.toLowerCase().includes(searchLower) ||
      item.BillNumber?.toString().includes(searchLower)
    );
  });

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  const columns = [
    { field: 'BillNumber', header: 'Bill No' },
    { field: 'BillDate', header: 'Bill Date' },
    { field: 'CustomerName', header: 'Customer Name' },
    { field: 'ContactNumber1', header: 'Contact' },
    { field: 'Location', header: 'Location' },
    { field: 'TotalAmount', header: 'Total Amount' },
    { field: 'PaidAmount', header: 'Paid Amount' },
    { field: 'BalanceAmount', header: 'Balance' },
  ];

  const renderActions = (item) => (
    <div style={{ display: 'flex', gap: '2px', alignItems: 'center', justifyContent: 'flex-end' }}>
      <Tooltip title="View Invoice" arrow>
        <Button
          size="small"
          onClick={() => handleOpenModal('view', item)}
          style={{
            minWidth: '22px',
            height: '22px',
            borderRadius: '6px',
            color: 'white',
            padding: '2px',
            boxShadow: '0 1px 4px rgba(102, 126, 234, 0.3)',
            border: 'none',
            fontSize: '10px',
            fontWeight: '600'
          }}
        >
          👁️
        </Button>
      </Tooltip>

      <Tooltip title="Edit Invoice" arrow>
        <Button
          size="small"
          onClick={() => handleOpenModal('edit', item)}
          style={{
            minWidth: '22px',
            height: '22px',
            borderRadius: '6px',
            color: 'white',
            padding: '2px',
            boxShadow: '0 1px 4px rgba(17, 153, 142, 0.3)',
            border: 'none',
            fontSize: '10px',
            fontWeight: '600'
          }}
        >
          ✏️
        </Button>
      </Tooltip>

      <Tooltip title="Print Invoice" arrow>
        <Button
          size="small"
          onClick={() => handleOpenPDFViewer(item.Id)}
          style={{
            minWidth: '22px',
            height: '22px',
            borderRadius: '6px',
            color: 'white',
            padding: '2px',
            boxShadow: '0 1px 4px rgba(245, 158, 11, 0.3)',
            border: 'none',
            fontSize: '10px',
            fontWeight: '600'
          }}
        >
          🖨️
        </Button>
      </Tooltip>

      <Tooltip title="Send WhatsApp" arrow>
        <Button
          size="small"
          onClick={() => handleWhatsApp(item)}
          style={{
            minWidth: '22px',
            height: '22px',
            borderRadius: '6px',
            color: 'white',
            padding: '2px',
            boxShadow: '0 1px 4px rgba(37, 211, 102, 0.3)',
            border: 'none',
            fontSize: '10px',
            fontWeight: '600',
            backgroundColor: '#25D366'
          }}
        >
          <WhatsAppIcon style={{ fontSize: '12px' }} />
        </Button>
      </Tooltip>

      <Tooltip title="Delete Invoice" arrow>
        <Button
          size="small"
          onClick={() => openConfirmDialog(item.Id)}
          style={{
            minWidth: '22px',
            height: '22px',
            borderRadius: '6px',
            color: 'white',
            padding: '2px',
            boxShadow: '0 1px 4px rgba(255, 107, 107, 0.3)',
            border: 'none',
            fontSize: '10px',
            fontWeight: '600'
          }}
        >
          🗑️
        </Button>
      </Tooltip>
    </div>
  );

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  return (
    <Fade in={mounted} timeout={800}>
      <div style={{
        minWidth: '100%',
        background: '#f5f7fa',
        padding: '10px',
        position: 'relative'
      }}>
        <style>{`
          @keyframes slideIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .action-button {
            text-transform: none;
            font-weight: 600;
            letter-spacing: 0.5px;
          }
          .table-row {
            /* No hover animations */
          }
        `}</style>

        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          padding: '12px',
          position: 'relative',
          color: 'black',
          fontFamily: 'Times New Romans',
        }}>
          {/* Header Section */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '10px',
            paddingBottom: '8px',
            borderBottom: '1px solid #f0f0f0'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div>
                <h4 style={{
                  fontSize: '17px',
                  fontWeight: '700',
                  margin: 0,
                  letterSpacing: '-0.3px',
                  fontFamily: 'Times New Romans',
                  textTransform: 'uppercase'
                }}>
                  <span style={{ color: 'black', fontFamily: 'Times New Romans', }}>Rent Invoices</span>
                </h4>
              </div>
            </div>
          </div>

          {/* Date Filters, Search - Horizontally Scrollable */}
          <div style={{
            marginBottom: '12px',
            overflowX: 'auto',
            overflowY: 'hidden',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'thin'
          }}>
            <div style={{
              display: 'flex',
              gap: '8px',
              alignItems: 'flex-end',
              minWidth: 'fit-content',
              paddingBottom: '4px'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: isMobile ? '120px' : '140px' }}>
                <label style={{ fontSize: '10px', color: '#666', paddingLeft: '4px' }}>From Date</label>
                <TextField
                  type="date"
                  size="small"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  sx={{
                    width: isMobile ? '120px' : '140px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      backgroundColor: '#f8f9fa',
                      fontSize: '11px',
                      height: '32px',
                      '&:hover fieldset': { borderColor: '#667eea' },
                      '&.Mui-focused fieldset': { borderColor: '#667eea' },
                    },
                  }}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: isMobile ? '120px' : '140px' }}>
                <label style={{ fontSize: '10px', color: '#666', paddingLeft: '4px' }}>To Date</label>
                <TextField
                  type="date"
                  size="small"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  sx={{
                    width: isMobile ? '120px' : '140px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      backgroundColor: '#f8f9fa',
                      fontSize: '11px',
                      height: '32px',
                      '&:hover fieldset': { borderColor: '#667eea' },
                      '&.Mui-focused fieldset': { borderColor: '#667eea' },
                    },
                  }}
                />
              </div>

              {/* Search - Icon on mobile, always visible on desktop */}
              {isMobile ? (
                searchExpanded ? (
                  <TextField
                    size="small"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    autoFocus
                    onBlur={() => {
                      if (!searchTerm) {
                        setSearchExpanded(false);
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon style={{ color: '#667eea', fontSize: '18px' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      minWidth: '250px',
                      width: '350px',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px',
                        backgroundColor: '#f8f9fa',
                        fontSize: '12px',
                        height: '32px',
                        '&:hover fieldset': {
                          borderColor: '#667eea',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#667eea',
                        },
                      },
                    }}
                  />
                ) : (
                  <Tooltip title="Search" arrow>
                    <Button
                      onClick={() => setSearchExpanded(true)}
                      style={{
                        minWidth: '32px',
                        width: '32px',
                        height: '32px',
                        padding: '0',
                        borderRadius: '6px',
                        backgroundColor: '#f8f9fa',
                        border: '1px solid #e2e8f0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <SearchIcon style={{ color: '#667eea', fontSize: '18px' }} />
                    </Button>
                  </Tooltip>
                )
              ) : (
                <TextField
                  size="small"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon style={{ color: '#667eea', fontSize: '18px' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    flex: 1,
                    minWidth: '250px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      backgroundColor: '#f8f9fa',
                      fontSize: '12px',
                      height: '32px',
                      '&:hover fieldset': {
                        borderColor: '#667eea',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#667eea',
                      },
                    },
                  }}
                />
              )}
            </div>
          </div>

          {/* Table */}
          <div style={{ marginBottom: '10px' }}>
            <TableContainer component={Paper} style={{
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
              borderRadius: '10px',
              overflow: 'hidden'
            }}>
              <Table sx={{ minWidth: 650 }} aria-label="rent invoice table">
                <TableHead>
                  <TableRow style={{
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    height: '28px'
                  }}>
                    {columns.map((column) => (
                      <TableCell key={column.field} style={{
                        color: 'white',
                        fontWeight: '600',
                        fontSize: '10px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.3px',
                        padding: '4px 8px',
                        textAlign: column.field.includes('Amount') ? 'right' : 'left',
                        height: '28px'
                      }}>
                        {column.header}
                      </TableCell>
                    ))}
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '10px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      padding: '4px 8px',
                      textAlign: 'right',
                      height: '28px'
                    }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.map((item, index) => (
                    <TableRow key={item.Id} className="table-row" style={{
                      background: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
                      borderBottom: '2px solid #f0f0f0'
                    }}>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                        {item.BillNumber || 'N/A'}
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                        {formatDate(item.BillDate)}
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                        {item.CustomerName || 'N/A'}
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                        {item.ContactNumber1 || 'N/A'}
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                        {item.Location || 'N/A'}
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', textAlign: 'right', fontWeight: 600 }}>
                        {formatCurrency(item.TotalAmount)}
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#10b981', textAlign: 'right', fontWeight: 600 }}>
                        {formatCurrency(item.PaidAmount)}
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: item.BalanceAmount > 0 ? '#ef4444' : '#10b981', textAlign: 'right', fontWeight: 600 }}>
                        {formatCurrency(item.BalanceAmount)}
                      </TableCell>
                      <TableCell style={{ padding: '8px 12px', textAlign: 'right' }}>
                        {renderActions(item)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            pageSizeOptions={[10, 25, 50, 100]}
          />
        </div>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={confirmDialogOpen}
          onClose={closeConfirmDialog}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          PaperProps={{
            style: {
              borderRadius: '12px',
              padding: '8px'
            }
          }}
        >
          <DialogTitle id="alert-dialog-title" style={{
            fontSize: '16px',
            fontWeight: '600',
            color: '#2d3748'
          }}>
            Delete Invoice?
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description" style={{
              fontSize: '13px',
              color: '#666'
            }}>
              Are you sure you want to delete this rent invoice?
            </DialogContentText>
          </DialogContent>
          <DialogActions style={{ padding: '8px 12px', gap: '6px' }}>
            <Button
              onClick={closeConfirmDialog}
              style={{
                background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
                color: '#ffffff',
                borderRadius: '6px',
                padding: '4px 8px',
                fontSize: '12px',
                fontWeight: '600',
                textTransform: 'none',
                boxShadow: '0 1px 4px rgba(255, 107, 107, 0.3)',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                minHeight: '26px'
              }}
            >
              <span style={{ fontSize: '12px' }}>❌</span>
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              style={{
                background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                color: '#ffffff',
                borderRadius: '6px',
                padding: '4px 8px',
                fontSize: '12px',
                fontWeight: '600',
                textTransform: 'none',
                boxShadow: '0 1px 4px rgba(17, 153, 142, 0.3)',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                minHeight: '26px'
              }}
            >
              <span style={{ fontSize: '12px' }}>✅</span>
              Confirm Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Loading Backdrop */}
        <Backdrop
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={modalLoading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>

        {/* Rent Invoice Modal */}
        <RentInvoiceModal
          open={modalOpen}
          onClose={handleCloseModal}
          deliveryData={selectedItem}
          mode={modalMode}
          onSave={fetchTableData}
        />

        {/* PDF Viewer Modal */}
        <RentInvoicePDFViewer
          open={pdfViewerOpen}
          onClose={handleClosePDFViewer}
          rentInvoiceData={selectedInvoiceForPDF}
          rentInvoiceDetails={selectedInvoiceDetailsForPDF}
          companyData={companyData}
          items={items}
        />

        {/* Footer */}
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '12px 20px',
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: '16px',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
          fontFamily: 'serif',
          fontWeight: '600',
          color: '#2c3e50',
          fontSize: '13px',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          zIndex: 1000
        }}>
          <span style={{ fontSize: '18px' }}>©️</span>
          <span>EFFTABS SOLUTIONS LLP</span>
        </div>
      </div>
    </Fade>
  );
}

export default RentInvoiceIndex;
